/**
 * Polices de la plateforme : un CATALOGUE FERMÉ, servi par l'application
 * elle-même (paquets @fontsource), jamais un nom de famille libre ni un service
 * tiers. Choix délibéré : un appel à Google Fonts transmettrait l'adresse IP de
 * chaque visiteur (risque RGPD) et ferait dépendre l'affichage d'un tiers.
 *
 * Fonctions PURES sauf `chargerPolices`, qui déclenche le téléchargement des
 * fichiers. Chaque police est un fragment séparé (`import()` dynamique) : seule
 * la police CHOISIE est téléchargée, jamais tout le catalogue.
 */

/**
 * Identifiants du catalogue. MIROIR de `POLICES_TEXTE` / `POLICES_CODE`
 * (backend `core/apparence/schemas.ts`) et de la migration 0046 — un test
 * backend fige les trois. Un identifiant = le nom du paquet @fontsource, sauf
 * les polices du système, qui ne se téléchargent pas.
 */
export const POLICES_TEXTE = [
  'system',
  'inter',
  'open-sans',
  'source-sans-3',
  'ibm-plex-sans',
  'nunito-sans',
  'work-sans',
  'montserrat',
] as const
export const POLICES_CODE = [
  'system-mono',
  'jetbrains-mono',
  'ibm-plex-mono',
  'fira-code',
  'source-code-pro',
] as const

export type PoliceTexte = (typeof POLICES_TEXTE)[number]
export type PoliceCode = (typeof POLICES_CODE)[number]

export interface PolicesPlateforme {
  sans?: PoliceTexte
  mono?: PoliceCode
}

export const FAMILLES = ['sans', 'mono'] as const
export type Famille = (typeof FAMILLES)[number]

/** Polices de `assets/main.css` (vérifié par test) : les choisir = ne rien surcharger. */
export const DEFAUTS_POLICES = { sans: 'inter', mono: 'jetbrains-mono' } as const

/**
 * Piles de repli de `main.css`, recopiées à l'identique (vérifié par test) :
 * pendant le téléchargement, ou s'il échoue, le texte s'affiche dans la police
 * du système — jamais dans une police par défaut du navigateur à empattements.
 */
const REPLI_TEXTE =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif"
const REPLI_CODE =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace"

interface Police {
  /** Nom de famille déclaré par les `@font-face` du paquet ; `null` = système. */
  famille: string | null
  /**
   * Télécharge les graisses employées par l'interface. Chemins LITTÉRAUX : Vite
   * ne découpe en fragments que des `import()` qu'il peut lire à la compilation.
   * Texte : 400 à 700 (`--font-regular` à `--font-bold`) ; code : 400.
   */
  charger: () => Promise<unknown>
}

const aucun = async (): Promise<void> => {}

export const CATALOGUE_TEXTE: Readonly<Record<PoliceTexte, Police>> = {
  system: { famille: null, charger: aucun },
  inter: {
    famille: 'Inter',
    charger: () =>
      Promise.all([
        import('@fontsource/inter/400.css'),
        import('@fontsource/inter/500.css'),
        import('@fontsource/inter/600.css'),
        import('@fontsource/inter/700.css'),
      ]),
  },
  'open-sans': {
    famille: 'Open Sans',
    charger: () =>
      Promise.all([
        import('@fontsource/open-sans/400.css'),
        import('@fontsource/open-sans/500.css'),
        import('@fontsource/open-sans/600.css'),
        import('@fontsource/open-sans/700.css'),
      ]),
  },
  'source-sans-3': {
    famille: 'Source Sans 3',
    charger: () =>
      Promise.all([
        import('@fontsource/source-sans-3/400.css'),
        import('@fontsource/source-sans-3/500.css'),
        import('@fontsource/source-sans-3/600.css'),
        import('@fontsource/source-sans-3/700.css'),
      ]),
  },
  'ibm-plex-sans': {
    famille: 'IBM Plex Sans',
    charger: () =>
      Promise.all([
        import('@fontsource/ibm-plex-sans/400.css'),
        import('@fontsource/ibm-plex-sans/500.css'),
        import('@fontsource/ibm-plex-sans/600.css'),
        import('@fontsource/ibm-plex-sans/700.css'),
      ]),
  },
  'nunito-sans': {
    famille: 'Nunito Sans',
    charger: () =>
      Promise.all([
        import('@fontsource/nunito-sans/400.css'),
        import('@fontsource/nunito-sans/500.css'),
        import('@fontsource/nunito-sans/600.css'),
        import('@fontsource/nunito-sans/700.css'),
      ]),
  },
  'work-sans': {
    famille: 'Work Sans',
    charger: () =>
      Promise.all([
        import('@fontsource/work-sans/400.css'),
        import('@fontsource/work-sans/500.css'),
        import('@fontsource/work-sans/600.css'),
        import('@fontsource/work-sans/700.css'),
      ]),
  },
  montserrat: {
    famille: 'Montserrat',
    charger: () =>
      Promise.all([
        import('@fontsource/montserrat/400.css'),
        import('@fontsource/montserrat/500.css'),
        import('@fontsource/montserrat/600.css'),
        import('@fontsource/montserrat/700.css'),
      ]),
  },
}

export const CATALOGUE_CODE: Readonly<Record<PoliceCode, Police>> = {
  'system-mono': { famille: null, charger: aucun },
  'jetbrains-mono': {
    famille: 'JetBrains Mono',
    charger: () => import('@fontsource/jetbrains-mono/400.css'),
  },
  'ibm-plex-mono': {
    famille: 'IBM Plex Mono',
    charger: () => import('@fontsource/ibm-plex-mono/400.css'),
  },
  'fira-code': {
    famille: 'Fira Code',
    charger: () => import('@fontsource/fira-code/400.css'),
  },
  'source-code-pro': {
    famille: 'Source Code Pro',
    charger: () => import('@fontsource/source-code-pro/400.css'),
  },
}

function police(famille: Famille, id: string): Police {
  const catalogue: Readonly<Record<string, Police>> =
    famille === 'sans' ? CATALOGUE_TEXTE : CATALOGUE_CODE
  const trouvee = catalogue[id]
  if (trouvee === undefined) throw new Error(`Police hors catalogue : ${JSON.stringify(id)}`)
  return trouvee
}

/**
 * Valeur CSS de `--font-sans` / `--font-mono` pour une police du catalogue.
 * Le nom de famille vient du CATALOGUE (une constante du code), jamais de la
 * valeur reçue : c'est ce qui rend l'injection impossible ici.
 */
export function pileCss(famille: Famille, id: string): string {
  const { famille: nom } = police(famille, id)
  const repli = famille === 'sans' ? REPLI_TEXTE : REPLI_CODE
  return nom === null ? repli : `'${nom}', ${repli}`
}

/** Valide une forme reçue et la recopie, ou `null` si UN élément est hors contrat. */
export function normaliserPolices(brut: unknown): PolicesPlateforme | null {
  if (typeof brut !== 'object' || brut === null || Array.isArray(brut)) return null
  const resultat: PolicesPlateforme = {}
  for (const [cle, valeur] of Object.entries(brut)) {
    if (cle === 'sans' && (POLICES_TEXTE as readonly unknown[]).includes(valeur)) {
      resultat.sans = valeur as PoliceTexte
    } else if (cle === 'mono' && (POLICES_CODE as readonly unknown[]).includes(valeur)) {
      resultat.mono = valeur as PoliceCode
    } else {
      return null
    }
  }
  return resultat
}

/** Jetons CSS des polices surchargées (rien pour une famille non réglée). */
export function jetonsPolices(polices: PolicesPlateforme): Record<string, string> {
  const jetons: Record<string, string> = {}
  for (const famille of FAMILLES) {
    const id = polices[famille]
    if (id !== undefined) jetons[`--font-${famille}`] = pileCss(famille, id)
  }
  return jetons
}

/**
 * `html:root` (0,1,1), comme les arrondis : l'emporte sur `:root` de
 * `main.css` quel que soit l'ordre d'insertion des feuilles, et le contraste
 * renforcé ne touche pas aux polices.
 */
export const SELECTEUR_POLICES = 'html:root'

/** Feuille des polices, `''` sans surcharge. Lève sur une police hors catalogue. */
export function feuillePolices(polices: PolicesPlateforme): string {
  const lignes = Object.entries(jetonsPolices(polices)).map(([n, v]) => `  ${n}: ${v};`)
  return lignes.length === 0 ? '' : `${SELECTEUR_POLICES} {\n${lignes.join('\n')}\n}`
}

/**
 * Télécharge les polices EFFECTIVES (surcharge, sinon défaut). Les défauts sont
 * déjà importés par `main.ts` : les redemander ne coûte rien, le module est en
 * cache. Rejette si un fichier n'a pas pu être chargé ; l'appelant le signale,
 * le texte restant lisible dans la pile de repli.
 */
export async function chargerPolices(polices: PolicesPlateforme): Promise<void> {
  await Promise.all([
    police('sans', polices.sans ?? DEFAUTS_POLICES.sans).charger(),
    police('mono', polices.mono ?? DEFAUTS_POLICES.mono).charger(),
  ])
}
